import { Component, OnInit } from '@angular/core';

// ANIMATIONS

// COMPONENTS

// CONFIGS

// INTERFACES

// LIBS
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

// SERVICES
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ScannerQRCodeConfig } from 'ngx-scanner-qrcode';
import { Observable, Subject, throwError } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { IStock } from '../../../rfq/interfaces/rfq-stock.interface';
import { LocalStorageKeys } from '../../../shared';
import { slideInAnimation } from '../../../shared/animations/animation';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import {
  EScannerType,
  IScannerResponse,
} from '../../../shared/interfaces/common.interface';
import { InventoryApiService } from '../../../shared/services/api/inventory-api.service';
import { RfqStockLocationApiService } from '../../../shared/services/api/rfq/rfq-stock-location-api.service';
import {
  UiConfirmationDialogComponent,
  UiWarningDialogComponent,
} from '../../../ui/components';

@Component({
  selector: 'lum-location-warehouse-app-scanner',
  templateUrl: './location-warehouse-app-scanner.component.html',
  styleUrls: ['./location-warehouse-app-scanner.component.scss'],
  animations: [slideInAnimation],
})
export class LocationWarehouseAppScannerComponent implements OnInit {
  loading :boolean = false;

  scannerConfig :ScannerQRCodeConfig = {
    fps: 10,
    vibrate: 300,
    isBeep: true,
    constraints: { audio: false, video: true },
  };
  rfqStockLocationId :number;
  quantity :number;
  relations :string = 'location,rfqStock,rfqStock.rfq';
  stock :IStock;
  activeDevice :string;

  private onDestroy$ = new Subject();

  constructor(
    private router :Router,
    private route :ActivatedRoute,
    private dialog :MatDialog,
    private rfqStockLocationApiService :RfqStockLocationApiService,
    private inventoryApiservice :InventoryApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getRouterQueryParams().subscribe();
    this.getRouterParams().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onScanned(data :IScannerResponse) :void {
    if (this.rfqStockLocationId && this.quantity) {
      if (data.type === EScannerType.LOCATION) {
        this.transfer(data);
      } else {
        this.dialog.open(UiWarningDialogComponent, {
          data: {
            title: 'Warning',
            message: `Type: ${data.type} is not a valid location. Please scan a location to continue.`,
          },
          width: DialogConfig.sizes.small,
        });
      }
    } else {
      switch (data.type) {
        case EScannerType.RFQ:
          this.router.navigate([
            '/warehouse-mobile-app/location-selection',
            data.id,
          ]);
          break;
        case EScannerType.LOCATION:
          this.router.navigate(['/warehouse-mobile-app/sku-selection', data.id]);
          break;
        case EScannerType.ORDER_NOT_IN_PICKING:
          this.router.navigateByUrl(
            `/shipping/posted-orders?warehouseGroup=${data.warehouseGroupId}&orderNumber=${data.orderNumber}`,
          );
          break;
        case EScannerType.ORDER_SHIPPED:
          this.router.navigateByUrl(
            `/shipping/posted-orders?warehouseGroup=${data.warehouseGroupId}&orderNumber=${data.orderNumber}`,
          );
          break;
        case EScannerType.ORDER_PICKLIST_CREATED:
          this.router.navigate(['/shipping/visual-picking', data.id]);
          break;
        case EScannerType.ORDER_PICKLIST_PICKED:
          this.router.navigate(['/shipping/picklists', data.id]);
          break;
        default:
          this.dialog.open(UiWarningDialogComponent, {
            data: {
              title: 'Warning',
              message: `Type: ${data.type} is not a valid type. Please scan a valid type to continue.`,
            },
            width: DialogConfig.sizes.small,
          });
          break;
      }
    }
  }

  private getRouterParams() :Observable<Params> {
    return this.route.params.pipe(
      tap((params :Params) => {
        if (params['rfq_stock_location_id']) {
          this.rfqStockLocationId = params['rfq_stock_location_id'];
          this.loading = true;
          this.getRfqStock(this.rfqStockLocationId);
        }
        if (params['quantity']) {
          this.quantity = params['quantity'];
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  // get route query params
  private getRouterQueryParams() :Observable<Params> {
    return this.route.queryParams.pipe(
      tap((params :Params) => {
        const { deviceId } = params;
        if (deviceId) {
          this.activeDevice = deviceId;
        } else {
          const deviceId :string | null = localStorage.getItem(
            LocalStorageKeys.WAREHOUSE_APP_ACTIVE_DEVICE_ID_KEY,
          );
          if (deviceId) {
            this.activeDevice = deviceId;
          } else {
            this.router.navigate(['/warehouse-mobile-app/devices']);
          }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRfqStock(id :number) :void {
    this.rfqStockLocationApiService
      .getById({
        id,
        params: { relations: this.relations },
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.stock = response;
          this.loading = false;
        },
        error: (error) => {
          this.loading = false;

          return error;
        },
      });
  }

  private transfer(destinationLocation :IScannerResponse) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Action',
          message: `Transfer ${this.quantity} from ${this.stock.location.locationName} to ${destinationLocation.scannedValue} ?`,
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loading = true;
          this.inventoryApiservice
            .moveSkuToLocation({
              rfqStockLocationIds: [this.stock.id],
              sourceLocationId: this.stock.locationId,
              destinationLocationId: destinationLocation.id,
              quantity: this.quantity,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: () => {
                this.loading = false;
                this.snackBar.open('Transfer successful', 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.router.navigate(['/warehouse-mobile-app/devices']);
              },
              error: (error) => {
                this.loading = false;

                return error;
              },
            });
        }
      });
  }

  get title() :string {
    return this.rfqStockLocationId && this.quantity
      ? 'Scan QR - Transfer SKU'
      : 'Scan QR';
  }
}
